import { ProvedorAvisos } from '@baiocchi/ui'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'

import { App } from './App'
import { ErroApi } from './api/erros'
import { ProvedorSessao } from './autenticacao/contexto-sessao'
import './estilos/global.css'

/**
 * Cliente de consultas.
 *
 * `retry` é seletivo: repetir um `403` ou um `422` não muda o resultado — o papel
 * não vai mudar e o dado enviado é o mesmo — e só faz o usuário esperar três vezes
 * pelo mesmo erro. Falha de rede e `5xx`, sim, valem uma segunda tentativa.
 *
 * `refetchOnWindowFocus` fica ligado de propósito: o CRM fica aberto o dia inteiro
 * em uma aba, e voltar para ela depois de duas horas com a lista de leads de duas
 * horas atrás é pior do que uma consulta a mais.
 */
const clienteConsulta = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: true,
      retry: (tentativa, erro) => {
        if (erro instanceof ErroApi) {
          if (erro.status >= 400 && erro.status < 500) return false
          return tentativa < 1
        }
        return tentativa < 2
      },
    },
    mutations: {
      // Mutação **nunca** é repetida automaticamente: quase toda escrita aqui grava
      // uma interação em tabela append-only (RN-25). Uma segunda tentativa de um
      // `POST /leads/{uuid}/interacoes` que na verdade funcionou deixaria a timeline
      // com o mesmo atendimento duas vezes, e ninguém pode apagar.
      retry: false,
    },
  },
})

const raiz = document.getElementById('raiz')
if (!raiz) throw new Error('Elemento #raiz não encontrado em index.html.')

createRoot(raiz).render(
  <StrictMode>
    <QueryClientProvider client={clienteConsulta}>
      <BrowserRouter>
        <ProvedorAvisos>
          <ProvedorSessao>
            <App />
          </ProvedorSessao>
        </ProvedorAvisos>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
)
